import { findFencedBlock } from './factory-questions';

/**
 * T-55: further changes the software factory's AI builder suggests on a task mirrored from jflo. The
 * factory posts a comment holding one fenced block, the one its coding agent writes
 * (software-factory's shared/factory-suggestions.ts, kept in step with it):
 *
 * ```factory-suggestions
 * { "suggestions": [{ "id": "retry", "title": "Retry the sync on a timeout", "detail": "…" }] }
 * ```
 *
 * The task shows each suggestion with what can be decided about it from jflo (the choices the factory
 * reads back from a mirrored task: Yes please, Backlog, No thanks). People pick one per suggestion and
 * apply them together, as one comment in the factory's decisions format (formatSuggestionDecisions),
 * which the card reads back (parseSuggestionDecisions).
 */

export const FACTORY_SUGGESTIONS_LANG = 'factory-suggestions';

/** Most suggestions in one block, and characters per piece of text. */
export const FACTORY_SUGGESTIONS_MAX = 10;
const TITLE_MAX = 200;
const DETAIL_MAX = 2000;
/** Characters in a decision's note (why not). */
export const SUGGESTION_NOTE_MAX = 1000;

export interface FactorySuggestion {
  id: string;
  title: string;
  /** Why, and what it would involve. */
  detail?: string;
}

export interface FactorySuggestions {
  suggestions: FactorySuggestion[];
}

/**
 * What can be decided about a suggestion from jflo:
 * - yes: the AI builder builds it on this task's branch and PR.
 * - backlog: a new task for later.
 * - no: we decided not to; the note says why (optional).
 */
export const SUGGESTION_ACTIONS = ['yes', 'backlog', 'no'] as const;
export type SuggestionAction = (typeof SUGGESTION_ACTIONS)[number];

export const SUGGESTION_ACTION_LABELS: Record<SuggestionAction, string> = {
  yes: 'Yes please',
  backlog: 'Backlog',
  no: 'No thanks',
};

const text = (v: unknown, max: number): string => (typeof v === 'string' ? v.trim().slice(0, max) : '');

/** The block's JSON, checked and trimmed; null when it isn't usable. */
export function parseFactorySuggestionsJson(raw: string): FactorySuggestions | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  // A bare list of suggestions is accepted too.
  const obj = (Array.isArray(data) ? { suggestions: data } : data) as Record<string, unknown> | null;
  if (!obj || typeof obj !== 'object' || !Array.isArray(obj.suggestions)) return null;
  const suggestions: FactorySuggestion[] = [];
  const ids = new Set<string>();
  for (const [i, item] of obj.suggestions.slice(0, FACTORY_SUGGESTIONS_MAX).entries()) {
    const s = (typeof item === 'string' ? { title: item } : (item ?? {})) as Record<string, unknown>;
    // Titles are one line: they're the key the decisions message shows.
    const title = text(s.title, TITLE_MAX).replace(/\s*\n\s*/g, ' ');
    if (!title) continue;
    let id = text(s.id, 60) || `s${i + 1}`;
    if (ids.has(id)) id = `${id}-${i + 1}`;
    ids.add(id);
    const detail = text(s.detail ?? s.description, DETAIL_MAX);
    suggestions.push({ id, title, ...(detail ? { detail } : {}) });
  }
  return suggestions.length ? { suggestions } : null;
}

/** The first factory-suggestions block in a message, with where it sits; null when there's none (or it's broken). */
export function findFactorySuggestions(message: string): { suggestions: FactorySuggestions; start: number; end: number } | null {
  const block = findFencedBlock(message, FACTORY_SUGGESTIONS_LANG);
  const suggestions = block ? parseFactorySuggestionsJson(block.body) : null;
  if (!block || !suggestions) return null;
  return { suggestions, start: block.start, end: block.end };
}

/** One suggestion's decision. `number` is its place in the block (1-based); `note` is why not. */
export interface SuggestionDecision {
  number: number;
  title: string;
  action: SuggestionAction;
  note: string;
}

const oneLine = (s: string) => s.replace(/\s+/g, ' ').trim();
const cleanTitle = (s: string) => oneLine(s).replace(/\*\*/g, '');

/** How a decisions message names the round it decides: the start of the round's comment id. */
export const suggestionsMessageRef = (eventId: string) => eventId.replace(/-/g, '').slice(0, 8).toLowerCase();

/**
 * The decisions as the comment the person posts (and the factory reads): `2. **Title** → No thanks: the reason`.
 * `messageRef` names the round when the task has more than one ("**Suggestions · 3f9a1c2e**"); without
 * it, the decisions are for the newest round before them.
 */
export function formatSuggestionDecisions(decisions: SuggestionDecision[], messageRef: string | null = null): string {
  const lines = [messageRef ? `**Suggestions · ${messageRef}**` : '**Suggestions**', ''];
  for (const d of [...decisions].sort((a, b) => a.number - b.number)) {
    const note = oneLine(d.note);
    lines.push(`${d.number}. **${cleanTitle(d.title)}** → ${SUGGESTION_ACTION_LABELS[d.action]}${note ? `: ${note}` : ''}`);
  }
  return lines.join('\n');
}

// The factory's own header may name the run ("**Suggestions from R-12 · 3f9a1c2e**"): read those too.
const HEADER_RE = /^\*\*Suggestions(?: from R-\d+)?(?: · ([0-9a-f]{8}))?\*\*$/;
const LABEL_ACTIONS = new Map(SUGGESTION_ACTIONS.map((a) => [SUGGESTION_ACTION_LABELS[a], a]));
const LINE_RE = new RegExp(`^(\\d+)\\.\\s+\\*\\*(.*?)\\*\\*\\s+→\\s+(${SUGGESTION_ACTIONS.map((a) => SUGGESTION_ACTION_LABELS[a]).join('|')})(?::\\s*(.*))?$`);

/** Reads back a decisions message: the round it names (null: none) and each decision; null when the message isn't one. */
export function parseSuggestionDecisions(message: string): { messageRef: string | null; decisions: SuggestionDecision[] } | null {
  const lines = message.replace(/\r\n?/g, '\n').trim().split('\n');
  const header = HEADER_RE.exec(lines[0]?.trim() ?? '');
  if (!header) return null;
  const decisions: SuggestionDecision[] = [];
  for (const line of lines.slice(1)) {
    const m = LINE_RE.exec(line.trim());
    if (!m) continue;
    decisions.push({ number: Number(m[1]), title: m[2], action: LABEL_ACTIONS.get(m[3])!, note: (m[4] ?? '').trim() });
  }
  return decisions.length ? { messageRef: header[1] ?? null, decisions } : null;
}
